import React from "react";
import PropTypes from "prop-types";
import FormControl from "react-bootstrap/FormControl";
import FormSelect from "react-bootstrap/FormSelect";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { snakeCase } from "lodash";

const {
  threat_name_platform: platforms,
  threat_name_subplatform: subplatforms,
  threat_name_type: threatNameType
} = window?.tcbase?.consts || {};

const composePlatformNames = () => {
  return platforms?.reduce((prev, curr) => {
    let suffixedPlatforms = [];
    if (subplatforms[curr].length > 0) {
      suffixedPlatforms = subplatforms[curr].map(
        subplatform => `${curr}-${subplatform}`
      );
      prev = prev.concat(suffixedPlatforms); // eslint-disable-line no-param-reassign
    } else {
      prev.push(curr);
    }
    return prev;
  }, []);
};

const platformNameChoices = id =>
  composePlatformNames().map(name => (
    <option
      id={`${id}--${name}`}
      value={name}
      key={name}
      data-testid={`platform_option_${snakeCase(name)}`}
    >
      {name}
    </option>
  ));

const typeNameChoices = id =>
  window.tcbase.consts.threat_name_type.map(name => (
    <option
      id={`${id}--${name}`}
      value={name}
      key={name}
      data-testid={`threat_name_option_${snakeCase(name)}`}
    >
      {name}
    </option>
  ));

export default class ThreatName extends React.Component {
  constructor(props) {
    super(props);
    this.value = props.value || props.defaultValue;
  }

  componentDidUpdate() {
    if (this.props.value) {
      this.value = this.props.value;
    }
  }

  onChange = event => this.props.onChange(this.createOnChangeEvent(event));

  createOnChangeEvent = event => {
    this.value = this.toRepresentation(event.target.value, event.target.name);

    const newEvent = Object.assign({}, event);
    newEvent.target = this;
    return newEvent;
  };

  toInternalValue = threatName => {
    const threatNameSplit = threatName ? threatName.split(".") : [];
    return {
      platform: threatNameSplit[0],
      type: threatNameSplit[1],
      family: threatNameSplit[2]
    };
  };

  toRepresentation = (value, part) => {
    const splitValue = this.value.split(".");
    if (part === "platform") {
      splitValue[0] = value;
    } else if (part === "type") {
      splitValue[1] = value;
    } else {
      splitValue[2] = value;
    }
    return splitValue.join(".");
  };

  render() {
    const { value, defaultValue } = this.props;
    const internalDefaultValue = this.toInternalValue(defaultValue);
    const internalValue = this.toInternalValue(value);

    return (
      <Row>
        <Col xs={4}>
          <FormSelect
            id={`${this.props.id}--threat-name-platform`}
            name="platform"
            onChange={this.onChange}
            value={internalValue.platform}
            defaultValue={internalDefaultValue.platform}
            data-testid="threat_name_select_platform"
          >
            <option disabled value="">
              Platform
            </option>
            {platformNameChoices(`${this.props.id}--threat-name-platform`)}
          </FormSelect>
        </Col>
        <Col xs={4}>
          <FormSelect
            id={`${this.props.id}--threat-name-type`}
            name="type"
            onChange={this.onChange}
            value={internalValue.type}
            defaultValue={internalDefaultValue.type}
            data-testid="threat_name_select_threat_type"
          >
            <option disabled value="">
              Type
            </option>
            {typeNameChoices(`${this.props.id}--threat-name-type`)}
          </FormSelect>
        </Col>
        <Col xs={4}>
          <FormControl
            id={`${this.props.id}--threat-name-family`}
            name="family"
            as="input"
            placeholder="Family name"
            onChange={this.onChange}
            value={internalValue.family}
            defaultValue={internalDefaultValue.family}
            data-testid="threat_name_threat_family"
          />
        </Col>
      </Row>
    );
  }
}

ThreatName.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func.isRequired
};
